import React, { useEffect, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Slide,
  Grid,
  Card,
  CardContent,
  Avatar,
  Button,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Paper,
  Tabs,
  Tab,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  Email,
  Phone,
  LocationOn,
  CalendarToday,
  Work,
  AttachMoney,
  Person,
  Business,
  FileDownload,
  CheckCircle,
  Cancel,
  AccessTime,
  Add,
  Edit,
  Delete,
  Star,
  StarBorder,
} from "@mui/icons-material";
import { recruitJobsApis } from "../../../Data/axios";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function JobApplicationViewModal({
  open,
  handleClose,
  viewData,
}) {
  const [workExperience, setWorkExperience] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  // Fetch work experience data
  useEffect(() => {
    const fetchWorkExperience = async () => {
      if (!viewData?.applicant_exp) return;

      try {
        setLoading(true);
        const response = await recruitJobsApis.getWorkExperience();
        // Find the specific work experience for the current applicant
        const applicantExperience =
          response.data.work_experiences?.data?.find(
            (exp) => exp.id === viewData.applicant_exp
          ) || null;
        setWorkExperience(applicantExperience);
        console.log("Work Experience:", applicantExperience);
      } catch (error) {
        console.error("Error fetching work experience:", error);
        setWorkExperience(null);
      } finally {
        setLoading(false);
      }
    };

    if (open && viewData) {
      fetchWorkExperience();
    }
  }, [open, viewData?.applicant_exp]);
  if (!viewData) return null;

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "Not specified";
    return `₹${amount.toLocaleString()} per Day`;
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "applied":
        return "#2196f3";
      case "interview":
        return "#ff9800";
      case "selected":
        return "#4caf50";
      case "rejected":
        return "#f44336";
      case "closed":
        return "#9e9e9e";
      default:
        return "#757575";
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    const d = new Date(dateString);
    if (isNaN(d)) return dateString;
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    return `${dd}-${mm}-${d.getFullYear()}`;
  };

  // Calculate age from DOB
  const calculateAge = (dob) => {
    if (!dob) return "Not specified";
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return `${age} years`;
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Modal open={open} onClose={handleClose} closeAfterTransition>
      <Slide direction="left" in={open}>
        <Box
          sx={{
            position: "absolute",
            top: 0,
            right: 0,
            width: "75%",
            height: "100%",
            bgcolor: "#f8f9fa",
            boxShadow: 24,
            overflow: "auto",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: "#ffffff",
              p: 3,
              borderBottom: "1px solid #e0e0e0",
              position: "sticky",
              top: 0,
              zIndex: 1,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography
                variant="h4"
                sx={{ color: "#1a1a1a", fontWeight: 600 }}
              >
                Job Applications
              </Typography>
              <Button
                onClick={handleClose}
                variant="outlined"
                sx={{
                  borderColor: "#d1d5db",
                  color: "#6b7280",
                  "&:hover": {
                    borderColor: "#9ca3af",
                    bgcolor: "#f9fafb",
                  },
                }}
              >
                Close
              </Button>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            {/* Applicant Header */}
            <Card
              sx={{
                mb: 3,
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                border: "1px solid #e5e7eb",
                bgcolor: "#ffffff", // Ensure white background
              }}
            >
              <CardContent sx={{ p: 3, bgcolor: "#ffffff" }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar
                    src={viewData.applicant_image || undefined}
                    sx={{
                      width: 80,
                      height: 80,
                      bgcolor: "#e5e7eb",
                      color: "#374151",
                      mr: 3,
                      fontSize: "2rem",
                      borderRadius: 1, // Makes it square instead of circular
                    }}
                  >
                    {!viewData.applicant_image &&
                      (viewData.applicant_name?.charAt(0)?.toUpperCase() ||
                        "A")}
                  </Avatar>
                  <Box>
                    <Typography
                      variant="h5"
                      sx={{ color: "#1a1a1a", fontWeight: 600, mb: 1 }}
                    >
                      {viewData.applicant_name || "Unknown Applicant"}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                      <Chip
                        label={viewData.status || "Applied"}
                        sx={{
                          bgcolor: getStatusColor(viewData.status),
                          color: "white",
                          fontWeight: 500,
                        }}
                        size="small"
                      />
                      <Typography variant="body2" sx={{ color: "#6b7280" }}>
                        Applied on {formatDate(viewData.applied_at)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Grid container spacing={3}>
              {/* Left Column - Applicant Details */}
              <Grid item xs={12} lg={8}>
                {/* Personal Information */}
                <Card
                  sx={{
                    mb: 3,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    border: "1px solid #e5e7eb",
                    bgcolor: "#ffffff", // Ensure white background
                  }}
                >
                  <CardContent sx={{ p: 3, bgcolor: "#ffffff" }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#1a1a1a", mb: 2, fontWeight: 600 }}
                    >
                      Personal Information
                    </Typography>
                    <Table
                      size="medium"
                      sx={{
                        bgcolor: "#ffffff",
                        "& .MuiTableRow-root": {
                          bgcolor: "transparent",
                        },
                      }}
                    >
                      <TableBody sx={{ bgcolor: "#ffffff" }}>
                        {[
                          {
                            label: "Job Title",
                            value: viewData.job_title || viewData.job_name,
                          },
                          {
                            label: "Applicant Email",
                            value: (
                              <Box display="flex" alignItems="center">
                                <Email
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#6b7280" }}
                                />
                                {viewData.applicant_email}
                              </Box>
                            ),
                          },
                          {
                            label: "Applicant Phone",
                            value: (
                              <Box display="flex" alignItems="center">
                                <Phone
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#6b7280" }}
                                />
                                {viewData.applicant_phone}
                              </Box>
                            ),
                          },
                          {
                            label: "Location",
                            value: (
                              <Box display="flex" alignItems="center">
                                <LocationOn
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#6b7280" }}
                                />
                                {viewData.applicant_current_location}
                              </Box>
                            ),
                          },
                          {
                            label: "Date Of Birth",
                            value: viewData.applicant_dob
                              ? formatDate(viewData.applicant_dob)
                              : "Not provided",
                          },
                          {
                            label: "Gender",
                            value: viewData.applicant_gender || "Not specified",
                          },
                          {
                            label: "Total Experience",
                            value: workExperience?.name
                              ? `${workExperience.name}`
                              : "Not specified",
                          },
                          {
                            label: "Current Location",
                            value: viewData.applicant_current_location,
                          },
                          {
                            label: "Current CTC",
                            value: formatCurrency(
                              viewData.applicant_current_ctc
                            ),
                          },
                          {
                            label: "Expected CTC",
                            value: formatCurrency(
                              viewData.applicant_expected_ctc
                            ),
                          },
                          {
                            label: "Notice Period",
                            value: (
                              <Box display="flex" alignItems="center">
                                <AccessTime
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#6b7280" }}
                                />
                                {viewData.applicant_notice_period ||
                                  "Not specified"}
                              </Box>
                            ),
                          },
                          {
                            label: "Applied At",
                            value: formatDate(viewData.applied_at),
                          },
                          {
                            label: "Current Status",
                            value: (
                              <Box display="flex" alignItems="center">
                                {viewData.status === "Applied" ? (
                                  <CheckCircle
                                    sx={{ color: "#10b981", mr: 1 }}
                                    fontSize="small"
                                  />
                                ) : (
                                  <Cancel
                                    sx={{ color: "#ef4444", mr: 1 }}
                                    fontSize="small"
                                  />
                                )}
                                {viewData.status || "Applied"}
                              </Box>
                            ),
                          },
                          {
                            label: "Cover Letter",
                            value: viewData.cover_letter ? (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownload />}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#d1d5db",
                                  color: "#374151",
                                  "&:hover": {
                                    borderColor: "#9ca3af",
                                    bgcolor: "#f9fafb",
                                  },
                                }}
                              >
                                Download
                              </Button>
                            ) : (
                              "Not provided"
                            ),
                          },
                          {
                            label: "Resume",
                            value: viewData.resume ? (
                              <Button
                                variant="outlined"
                                size="small"
                                startIcon={<FileDownload />}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#d1d5db",
                                  color: "#374151",
                                  "&:hover": {
                                    borderColor: "#9ca3af",
                                    bgcolor: "#f9fafb",
                                  },
                                }}
                              >
                                Download
                              </Button>
                            ) : (
                              "Not provided"
                            ),
                          },
                        ].map((row, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              bgcolor: index % 2 === 0 ? "#ffffff" : "#f9fafb",
                              "&:hover": {
                                bgcolor:
                                  index % 2 === 0 ? "#f9fafb" : "#f3f4f6",
                              },
                              "& td": {
                                borderBottom: "1px solid #e5e7eb",
                                py: 2,
                              },
                            }}
                          >
                            <TableCell
                              sx={{
                                fontWeight: 500,
                                color: "#374151",
                                width: "40%",
                                bgcolor: "inherit",
                              }}
                            >
                              {row.label}
                            </TableCell>
                            <TableCell
                              sx={{
                                color: "#1f2937",
                                bgcolor: "inherit",
                              }}
                            >
                              {row.value}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Photo Placeholder */}
              <Grid item xs={12} lg={4}>
                {/* <Card
                  sx={{
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                    border: "1px solid #e5e7eb",
                    bgcolor: "#ffffff", // Ensure white background
                  }}
                >
                  <CardContent
                    sx={{ p: 3, textAlign: "center", bgcolor: "#ffffff" }}
                  >
                    <Box
                      sx={{
                        width: "100%",
                        height: 250,
                        bgcolor: "#f3f4f6",
                        border: "2px dashed #d1d5db",
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      {viewData.applicant_image ? (
                        <img
                          src={viewData.applicant_image}
                          alt="Applicant"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            borderRadius: 8,
                          }}
                        />
                      ) : (
                        <Person sx={{ fontSize: 80, color: "#9ca3af" }} />
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: "#6b7280" }}>
                      {viewData.applicant_image
                        ? "Applicant Photo"
                        : "No photo provided"}
                    </Typography>
                  </CardContent>
                </Card> */}
              </Grid>
            </Grid>

            {/* Lower Section with Tabs */}
            <Card
              sx={{
                mt: 3,
                boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                border: "1px solid #e5e7eb",
                bgcolor: "#ffffff",
              }}
            >
              <Box sx={{ borderBottom: 1, borderColor: "#e5e7eb" }}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  sx={{
                    "& .MuiTab-root": {
                      textTransform: "none",
                      color: "#6b7280",
                      fontWeight: 500,
                      "&.Mui-selected": {
                        color: "#2563eb",
                      },
                    },
                    "& .MuiTabs-indicator": {
                      backgroundColor: "#2563eb",
                    },
                  }}
                >
                  <Tab label="Interview Schedule" />
                  <Tab label="Skills" />
                  <Tab label="Applicant Notes" />
                  <Tab label="Additional Information" />
                  <Tab label="Follow Up" />
                  {/* <Tab label="Resume" /> */}
                </Tabs>
              </Box>

              {/* Interview Schedule Tab */}
              <TabPanel value={tabValue} index={0}>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#1a1a1a", fontWeight: 600 }}
                    >
                      Interview Schedule
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      sx={{
                        bgcolor: "#2563eb",
                        textTransform: "none",
                        "&:hover": { bgcolor: "#1d4ed8" },
                      }}
                    >
                      Schedule Interview
                    </Button>
                  </Box>
                  <Paper
                    sx={{
                      bgcolor: "#f9fafb",
                      p: 3,
                      textAlign: "center",
                    }}
                  >
                    <CalendarToday
                      sx={{ fontSize: 48, color: "#9ca3af", mb: 2 }}
                    />
                    <Typography variant="body1" sx={{ color: "#6b7280" }}>
                      No interviews scheduled yet
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "#9ca3af", mt: 1 }}
                    >
                      Click "Schedule Interview" to add an interview
                    </Typography>
                  </Paper>
                </Box>
              </TabPanel>

              {/* Skills Tab */}
              <TabPanel value={tabValue} index={1}>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#1a1a1a", fontWeight: 600 }}
                    >
                      Skills
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      sx={{
                        bgcolor: "#2563eb",
                        color: "#ffffff",
                        textTransform: "none",
                        "&:hover": { bgcolor: "#1d4ed8" },
                      }}
                    >
                      Add Skill
                    </Button>
                  </Box>
                  {/* <Grid container spacing={2}>
                    {[
                      "JavaScript",
                      "React",
                      "Node.js",
                      "Database Management",
                      "Communication",
                    ].map((skill, index) => (
                      <Grid item xs={12} sm={6} key={skill}>
                        <Card sx={{ p: 2, border: "1px solid #e5e7eb" }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="body1"
                              sx={{ fontWeight: 500 }}
                            >
                              {skill}
                            </Typography>
                            <Box display="flex" alignItems="center">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <IconButton key={star} size="small">
                                  {star <= Math.floor(Math.random() * 5) + 1 ? (
                                    <Star
                                      sx={{ color: "#fbbf24", fontSize: 20 }}
                                    />
                                  ) : (
                                    <StarBorder
                                      sx={{ color: "#d1d5db", fontSize: 20 }}
                                    />
                                  )}
                                </IconButton>
                              ))}
                            </Box>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid> */}
                </Box>
              </TabPanel>

              {/* Applicant Notes Tab */}
              <TabPanel value={tabValue} index={2}>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#1a1a1a", fontWeight: 600 }}
                    >
                      Applicant Notes
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      sx={{
                        bgcolor: "#2563eb",
                        textTransform: "none",
                        "&:hover": { bgcolor: "#1d4ed8" },
                      }}
                    >
                      Add Note
                    </Button>
                  </Box>
                  <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Add notes about this applicant..."
                    variant="outlined"
                    sx={{
                      mb: 3,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "#ffffff",
                      },
                    }}
                  />
                  {/* <List>
                    {[
                      {
                        note: "Strong technical background in React and Node.js",
                        date: "2024-06-15",
                        author: "HR Manager",
                      },
                      {
                        note: "Good communication skills during phone screening",
                        date: "2024-06-14",
                        author: "Recruiter",
                      },
                    ].map((item, index) => (
                      <ListItem
                        key={index}
                        sx={{
                          bgcolor: "#f9fafb",
                          mb: 1,
                          borderRadius: 1,
                          border: "1px solid #e5e7eb",
                        }}
                        secondaryAction={
                          <Box>
                            <IconButton size="small">
                              <Edit sx={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton size="small">
                              <Delete sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemText
                          primary={item.note}
                          secondary={`${item.date} - ${item.author}`}
                          sx={{
                            "& .MuiListItemText-primary": { color: "#1f2937" },
                            "& .MuiListItemText-secondary": {
                              color: "#6b7280",
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List> */}
                </Box>
              </TabPanel>

              {/* Additional Information Tab */}
              <TabPanel value={tabValue} index={3}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "#1a1a1a", fontWeight: 600, mb: 3 }}
                  >
                    Additional Information
                  </Typography>
                  {/* <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ p: 3, border: "1px solid #e5e7eb" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Education
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b7280", mb: 1 }}
                        >
                          Degree: {viewData.education_degree || "Not specified"}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b7280", mb: 1 }}
                        >
                          Institution:{" "}
                          {viewData.education_institution || "Not specified"}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          Year: {viewData.education_year || "Not specified"}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card sx={{ p: 3, border: "1px solid #e5e7eb" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          Certifications
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {viewData.certifications ||
                            "No certifications listed"}
                        </Typography>
                      </Card>
                    </Grid>
                    <Grid item xs={12}>
                      <Card sx={{ p: 3, border: "1px solid #e5e7eb" }}>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 2 }}
                        >
                          References
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {viewData.references || "No references provided"}
                        </Typography>
                      </Card>
                    </Grid>
                  </Grid> */}
                </Box>
              </TabPanel>

              {/* Follow Up Tab */}
              <TabPanel value={tabValue} index={4}>
                <Box>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    mb={3}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#1a1a1a", fontWeight: 600 }}
                    >
                      Follow Up
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<Add />}
                      sx={{
                        bgcolor: "#2563eb",
                        textTransform: "none",
                        "&:hover": { bgcolor: "#1d4ed8" },
                      }}
                    >
                      Add Follow Up
                    </Button>
                  </Box>
                  {/* <Grid container spacing={2}>
                    {[
                      {
                        action: "Send follow-up email",
                        due: "2024-06-20",
                        status: "Pending",
                      },
                      {
                        action: "Schedule technical interview",
                        due: "2024-06-18",
                        status: "Completed",
                      },
                      {
                        action: "Reference check",
                        due: "2024-06-22",
                        status: "Pending",
                      },
                    ].map((item, index) => (
                      <Grid item xs={12} key={index}>
                        <Card sx={{ p: 2, border: "1px solid #e5e7eb" }}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Box>
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 500, mb: 1 }}
                              >
                                {item.action}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ color: "#6b7280" }}
                              >
                                Due: {item.due}
                              </Typography>
                            </Box>
                            <Chip
                              label={item.status}
                              size="small"
                              sx={{
                                bgcolor:
                                  item.status === "Completed"
                                    ? "#10b981"
                                    : "#f59e0b",
                                color: "white",
                              }}
                            />
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid> */}
                </Box>
              </TabPanel>

              {/* Resume Tab */}
              <TabPanel value={tabValue} index={5}>
                <Box>
                  <Typography
                    variant="h6"
                    sx={{ color: "#1a1a1a", fontWeight: 600, mb: 3 }}
                  >
                    Resume & Documents
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          border: "1px solid #e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        <FileDownload
                          sx={{ fontSize: 48, color: "#6b7280", mb: 2 }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Resume
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b7280", mb: 2 }}
                        >
                          {viewData.resume
                            ? "resume.pdf"
                            : "No resume uploaded"}
                        </Typography>
                        {viewData.resume && (
                          <Button
                            variant="outlined"
                            startIcon={<FileDownload />}
                            sx={{
                              textTransform: "none",
                              borderColor: "#d1d5db",
                              color: "#374151",
                            }}
                          >
                            Download
                          </Button>
                        )}
                      </Card>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Card
                        sx={{
                          p: 3,
                          border: "1px solid #e5e7eb",
                          textAlign: "center",
                        }}
                      >
                        <FileDownload
                          sx={{ fontSize: 48, color: "#6b7280", mb: 2 }}
                        />
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600, mb: 1 }}
                        >
                          Cover Letter
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: "#6b7280", mb: 2 }}
                        >
                          {viewData.cover_letter
                            ? "cover_letter.pdf"
                            : "No cover letter uploaded"}
                        </Typography>
                        {viewData.cover_letter && (
                          <Button
                            variant="outlined"
                            startIcon={<FileDownload />}
                            sx={{
                              textTransform: "none",
                              borderColor: "#d1d5db",
                              color: "#374151",
                            }}
                          >
                            Download
                          </Button>
                        )}
                      </Card>
                    </Grid>
                  </Grid>
                </Box>
              </TabPanel>
            </Card>
          </Box>
        </Box>
      </Slide>
    </Modal>
  );
}
